import { useState, useCallback, useRef, type ReactNode, type FormEvent } from 'react';
import { motion } from 'motion/react';
import { useReducedMotion } from '../hooks/useReducedMotion';
import { getAppConfig } from '../config';

// Auth configuration sourced from app.config.ts (single source of truth)
// NOTE: This is a UX gate, not security. The date is visible in the client bundle.
// For actual security, use server-side authentication (Cloudflare Access, Supabase Auth, etc.)
const config = getAppConfig();
const authConfig = config.auth;
const STORAGE_KEY = authConfig.storageKey;
const AUTH_VALUE = authConfig.authValue;
const SECRET_DATE = authConfig.secretDate;

interface PinGateProps {
  children: ReactNode;
}

function isAuthenticated(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === AUTH_VALUE;
  } catch {
    // localStorage not available (private browsing, etc.)
    return false;
  }
}

function setAuthenticated(): void {
  try {
    localStorage.setItem(STORAGE_KEY, AUTH_VALUE);
  } catch {
    // Ignore storage errors
  }
}

export function PinGate({ children }: PinGateProps) {
  // Skip gate if auth is disabled or bypassed in dev mode
  if (!authConfig.enabled || config.debug.bypassAuth) {
    return <>{children}</>;
  }

  return <PinGateInner>{children}</PinGateInner>;
}

function PinGateInner({ children }: PinGateProps) {
  const [authenticated, setAuthenticatedState] = useState(isAuthenticated);
  const [day, setDay] = useState('');
  const [month, setMonth] = useState('');
  const [year, setYear] = useState('');
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [shake, setShake] = useState(false);

  // Refs for auto-tab functionality
  const monthInputRef = useRef<HTMLInputElement>(null);
  const yearInputRef = useRef<HTMLInputElement>(null);

  // Reduced motion preference
  const prefersReducedMotion = useReducedMotion();

  // Validate date ranges
  const isValidDate = useCallback((d: string, m: string, y: string): { valid: boolean; message: string } => {
    const dayNum = parseInt(d, 10);
    const monthNum = parseInt(m, 10);
    const yearNum = parseInt(y, 10);

    if (isNaN(dayNum) || dayNum < 1 || dayNum > 31) {
      return { valid: false, message: 'Day must be between 1 and 31' };
    }
    if (isNaN(monthNum) || monthNum < 1 || monthNum > 12) {
      return { valid: false, message: 'Month must be between 1 and 12' };
    }
    if (isNaN(yearNum) || yearNum < 1900 || yearNum > 2100) {
      return { valid: false, message: 'Please enter a valid year' };
    }
    return { valid: true, message: '' };
  }, []);

  const handleSubmit = useCallback(
    (e: FormEvent) => {
      e.preventDefault();

      // Validate date ranges first
      const validation = isValidDate(day, month, year);
      if (!validation.valid) {
        setError(true);
        setErrorMessage(validation.message);
        setShake(true);
        setTimeout(() => setShake(false), 500);
        setTimeout(() => {
          setError(false);
          setErrorMessage('');
        }, 3000);
        return;
      }

      // Format input to match secret date format: DD-MM-YYYY
      const paddedDay = day.padStart(2, '0');
      const paddedMonth = month.padStart(2, '0');
      const enteredDate = `${paddedDay}-${paddedMonth}-${year}`;

      if (enteredDate === SECRET_DATE) {
        setAuthenticated();
        setAuthenticatedState(true);
      } else {
        setError(true);
        setErrorMessage("That's not our special date... try again 💔");
        setShake(true);
        setTimeout(() => setShake(false), 500);
        setTimeout(() => {
          setError(false);
          setErrorMessage('');
        }, 3000);
      }
    },
    [day, month, year, isValidDate]
  );

  // Auto-tab handlers
  const handleDayChange = useCallback((value: string) => {
    const numericValue = value.replace(/\D/g, '');
    setDay(numericValue);
    // Auto-advance to month when 2 digits entered
    if (numericValue.length === 2) {
      monthInputRef.current?.focus();
    }
  }, []);

  const handleMonthChange = useCallback((value: string) => {
    const numericValue = value.replace(/\D/g, '');
    setMonth(numericValue);
    // Auto-advance to year when 2 digits entered
    if (numericValue.length === 2) {
      yearInputRef.current?.focus();
    }
  }, []);

  const handleYearChange = useCallback((value: string) => {
    setYear(value.replace(/\D/g, ''));
  }, []);

  // Authenticated — render the app
  if (authenticated) {
    return <>{children}</>;
  }

  // Not authenticated — show PIN gate
  return (
    <div className="min-h-screen bg-bg-base flex flex-col items-center justify-center px-4">
      <motion.div
        initial={prefersReducedMotion ? false : { opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: prefersReducedMotion ? 0 : 0.5 }}
        className="w-full max-w-sm"
      >
        {/* Heart icon */}
        <motion.div
          initial={prefersReducedMotion ? false : { scale: 0 }}
          animate={{ scale: 1 }}
          transition={prefersReducedMotion ? { duration: 0 } : { delay: 0.2, type: 'spring', stiffness: 200 }}
          className="text-6xl text-center mb-6"
        >
          💕
        </motion.div>

        {/* Title */}
        <h1 className="text-2xl font-light text-white text-center mb-2">
          Our Love Map
        </h1>

        {/* Romantic prompt */}
        <p className="text-gray-400 text-center mb-8">
          Enter our special date to unlock your memories...
        </p>

        {/* Date form */}
        <motion.form
          onSubmit={handleSubmit}
          animate={shake && !prefersReducedMotion ? { x: [-10, 10, -10, 10, 0] } : {}}
          transition={{ duration: 0.4 }}
          className="space-y-6"
        >
          <div className="flex items-center justify-center gap-2">
            {/* Day */}
            <input
              type="text"
              inputMode="numeric"
              maxLength={2}
              placeholder="DD"
              value={day}
              onChange={(e) => handleDayChange(e.target.value)}
              className="w-16 h-14 bg-bg-surface border border-gray-700 rounded-lg text-white text-center text-xl font-medium focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/50 transition-colors"
              aria-label="Day"
              autoFocus
            />

            <span className="text-gray-500 text-2xl">/</span>

            {/* Month */}
            <input
              ref={monthInputRef}
              type="text"
              inputMode="numeric"
              maxLength={2}
              placeholder="MM"
              value={month}
              onChange={(e) => handleMonthChange(e.target.value)}
              className="w-16 h-14 bg-bg-surface border border-gray-700 rounded-lg text-white text-center text-xl font-medium focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/50 transition-colors"
              aria-label="Month"
            />

            <span className="text-gray-500 text-2xl">/</span>

            {/* Year */}
            <input
              ref={yearInputRef}
              type="text"
              inputMode="numeric"
              maxLength={4}
              placeholder="YYYY"
              value={year}
              onChange={(e) => handleYearChange(e.target.value)}
              className="w-24 h-14 bg-bg-surface border border-gray-700 rounded-lg text-white text-center text-xl font-medium focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/50 transition-colors"
              aria-label="Year"
            />
          </div>

          {/* Error message */}
          <motion.p
            initial={{ opacity: 0, height: 0 }}
            animate={{
              opacity: error ? 1 : 0,
              height: error ? 'auto' : 0,
            }}
            transition={{ duration: prefersReducedMotion ? 0 : 0.2 }}
            className="text-accent text-center text-sm"
          >
            {errorMessage}
          </motion.p>

          {/* Submit button */}
          <button
            type="submit"
            disabled={!day || !month || !year || year.length < 4}
            className="w-full h-12 bg-primary hover:bg-primary/90 disabled:bg-gray-700 disabled:cursor-not-allowed rounded-lg text-white font-medium transition-colors flex items-center justify-center gap-2"
          >
            <span>💕</span>
            <span>Unlock</span>
          </button>
        </motion.form>

        {/* Hint */}
        <p className="text-gray-600 text-xs text-center mt-8">
          Hint: When did our journey begin?
        </p>
      </motion.div>
    </div>
  );
}
